"use client";

import Image from 'next/image';
import Link from 'next/link';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { FiArrowLeft, FiShare2, FiClock, FiCalendar, FiTag } from 'react-icons/fi';

export default function BlogPostClient({ post, structuredData }) {
  // If post doesn't exist, show not found page
  if (!post) {
    return (
      <div>
        <Header />
        <div className="blog-container not-found">
          <h1>Blog Post Not Found</h1>
          <p>Sorry, the blog post you&apos;re looking for doesn&apos;t exist.</p>
          <Link href="/blog" className="btn secondary-btn">
            <FiArrowLeft /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div>
      {/* Article structured data */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData)
          }}
        />
      )}
      
      <Header />
      
      <article className="blog-post">
        {/* Hero/Banner */}
        <div className="blog-post-hero" style={{ backgroundImage: `url(${post.coverImage})` }}>
          <div className="blog-container">
            <div className="blog-post-meta">
              <div className="blog-post-category">
                <FiTag /> {post.category}
              </div>
              <div className="blog-post-date">
                <FiCalendar /> {post.publishDate}
              </div>
              <div className="blog-post-readtime">
                <FiClock /> {post.readTime}
              </div>
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            
            <div className="blog-post-author">
              <div className="blog-author-avatar">
                <Image 
                  src={post.authorAvatar} 
                  alt={post.author} 
                  width={60} 
                  height={60}
                />
              </div>
              <div className="blog-author-info">
                <div className="blog-author-name">{post.author}</div>
                <div className="blog-author-role">{post.authorRole}</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="blog-container">
          <div className="blog-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
          
          <div className="blog-post-footer">
            <Link href="/blog" className="btn secondary-btn">
              <FiArrowLeft /> Back to Blog
            </Link>
            <button 
                className="btn share-btn"
                onClick={() => {
                    navigator.share ? 
                    navigator.share({
                        title: document.title,
                        url: window.location.href
                    }) : 
                    navigator.clipboard.writeText(window.location.href).then(() => {
                        alert("Link copied to clipboard!");
                    });
                }}
                >
                <FiShare2 /> Share This Post
            </button>
          </div>
        </div>
      </article>
      
      <Footer />
      
      {/* Blog Post Styles */}
      <style jsx>{`
        .blog-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        .blog-post-hero {
          background-size: cover;
          background-position: center;
          position: relative;
          padding: 8rem 0 4rem;
          color: white;
        }
        
        .blog-post-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%);
          z-index: 1;
        }
        
        .blog-post-hero .blog-container {
          position: relative;
          z-index: 2;
        }
        
        .blog-post-meta {
          display: flex;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }
        
        .blog-post-meta > div {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .blog-post-category {
          background: var(--primary-color);
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-weight: 600;
        }
        
        .blog-post-title {
          font-size: 3rem;
          line-height: 1.2;
          font-weight: 800;
          margin-bottom: 2rem;
        }
        
        .blog-post-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .blog-author-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid white;
        }
        
        .blog-author-name {
          font-size: 1.125rem;
          font-weight: 600;
        }
        
        .blog-author-name a {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        
        .blog-author-name a:hover {
          color: var(--primary-color);
          text-decoration: underline;
        }
        
        .blog-container:not(.not-found) {
          padding-top: 4rem;
          padding-bottom: 4rem;
        }
        
        .blog-post-content {
          font-size: 1.125rem;
          line-height: 1.8;
          color: var(--text-primary);
        }
        
        .blog-post-content h2 {
          font-size: 2rem;
          font-weight: 700;
          margin: 2.5rem 0 1.5rem;
          color: var(--text-primary);
        }
        
        .blog-post-content h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 2rem 0 1rem;
          color: var(--text-primary);
        }
        
        .blog-post-content p {
          margin-bottom: 1.5rem;
        }

        .blog-post-content :global(.blog-note) {
          margin: 1.75rem 0;
          padding: 1.25rem 1.5rem;
          border: 1px solid var(--border-color);
          border-left: 4px solid var(--primary-color);
          border-radius: var(--radius-md);
          background: var(--background-alt);
          color: var(--text-primary);
        }

        .blog-post-content :global(.comparison-table-wrap) {
          overflow-x: auto;
          margin: 1.5rem 0 2rem;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          background: white;
        }

        .blog-post-content :global(table) {
          width: 100%;
          min-width: 760px;
          border-collapse: collapse;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .blog-post-content :global(th),
        .blog-post-content :global(td) {
          padding: 0.85rem 1rem;
          border-bottom: 1px solid var(--border-color);
          text-align: left;
          vertical-align: top;
        }

        .blog-post-content :global(th) {
          background: var(--background-alt);
          font-weight: 700;
          color: var(--text-primary);
        }

        .blog-post-content :global(tr:last-child td) {
          border-bottom: none;
        }

        /* -------- LISTS INSIDE INNER HTML (use :global to escape styled-jsx scope) -------- */
        /* Ordered lists: keep native numbering */
        .blog-post-content :global(ol) {
          list-style: decimal outside;
          margin-bottom: 1.5rem;
          padding-left: 2rem;
        }
        .blog-post-content :global(ol li::marker) {
          font-variant-numeric: tabular-nums;
        }

        /* Unordered lists: custom bullets that win over global resets */
        .blog-post-content :global(ul) {
          list-style: none;
          margin-bottom: 1.5rem;
          padding-left: 0;
        }
        .blog-post-content :global(ul > li) {
          position: relative;
          margin: 0.375rem 0;
          padding-left: 1.25rem;   /* text indent */
        }
        .blog-post-content :global(ul > li::before) {
          content: "";
          position: absolute;
          left: 0;
          top: 0.9em;              /* vertical centering */
          transform: translateY(-50%);
          width: 0.35em;
          height: 0.35em;
          border-radius: 9999px;   /* dot bullet */
          background: currentColor;
        }
        /* Nested UL: smaller hollow bullets */
        .blog-post-content :global(ul ul > li::before) {
          width: 0.3em;
          height: 0.3em;
          background: transparent;
          box-shadow: 0 0 0 1.5px currentColor inset;
        }

        /* OPTIONAL: Uncomment below to use dash markers instead of bullets
        .blog-post-content ul {
          list-style: none;
        }
        
        .blog-post-content ul > li::before {
          content: "–";
          color: var(--text-secondary);
          margin-right: 0.75rem;
          margin-left: -1.25rem;
        }
        
        .blog-post-content ul ul > li::before {
          content: "–";
          color: var(--text-secondary);
        }
        */
        
        .blog-post-footer {
          margin-top: 4rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border-color);
          display: flex;
          justify-content: space-between;
        }
        
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: var(--radius-md);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .secondary-btn {
          border: 1px solid var(--border-color);
          background: white;
          color: var(--text-primary);
        }
        
        .secondary-btn:hover {
          background: var(--background-alt);
        }
        
        .share-btn {
          background: var(--primary-color);
          color: white;
          border: none;
        }
        
        .share-btn:hover {
          background: var(--primary-dark);
        }
        
        .not-found {
          text-align: center;
          padding: 6rem 0;
        }
        
        .not-found h1 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }
        
        .not-found p {
          margin-bottom: 2rem;
          color: var(--text-secondary);
        }
        
        @media (max-width: 768px) {
          .blog-container {
            padding: 0 1rem;
          }
          
          .blog-post-title {
            font-size: 2rem;
          }
          
          .blog-post-meta {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .blog-post-footer {
            flex-direction: column;
            gap: 1rem;
          }
          
          .blog-post-footer .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
